import { useEffect, useState } from 'react';
import { ArrowLeft, FileText, FolderOpen, NotebookPen, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { showAlert, showConfirm } from '../lib/tg.js';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemGroupContent,
  ItemGroupEmpty,
  ItemGroupHeader,
  ItemHeader,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { ErrorBanner } from '@/components/feedback';

interface Project {
  id: string;
  productId: string | null;
  name: string;
  slug: string;
  prdMd: string;
  techspecMd: string;
  trackerKind?: 'yandex' | 'linear' | null;
  trackerRef?: string | null;
  updatedAt?: string | null;
}

interface ProductRef {
  id: string;
  name: string;
  slug: string;
}

interface ProjectNote {
  id: string;
  projectId: string;
  body: string;
  createdAt: string;
}

interface ProjectsViewProps {
  /** Optional — when omitted, view shows all projects with product column. */
  productId?: string;
  productName?: string;
  onBack?: () => void;
}

type DialogState =
  | { kind: 'edit'; project: Project | null }
  | { kind: 'prd'; project: Project }
  | { kind: 'techspec'; project: Project }
  | { kind: 'notes'; project: Project }
  | null;

export function ProjectsView({ productId, productName, onBack }: ProjectsViewProps) {
  const [items, setItems] = useState<Project[]>([]);
  const [products, setProducts] = useState<ProductRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [filter, setFilter] = useState<'all' | 'unlinked' | string>(productId ?? 'all');

  const reload = async () => {
    try {
      const url = productId
        ? `/api/projects?product_id=${encodeURIComponent(productId)}`
        : filter === 'all'
          ? '/api/projects'
          : filter === 'unlinked'
            ? '/api/projects?unlinked=true'
            : `/api/projects?product_id=${encodeURIComponent(filter)}`;
      const [projs, prods] = await Promise.all([
        api.get<Project[]>(url),
        api.get<ProductRef[]>('/api/products'),
      ]);
      setItems(projs);
      setProducts(prods);
    } catch (e) {
      setError((e as { message: string }).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [productId, filter]);

  /** When in product-scoped mode, lock the create dialog's productId. */
  const newProjectInitial = productId ? { productId } : undefined;
  const productMap = new Map(products.map((p) => [p.id, p]));

  const headerTitle = productId ? (
    <span className="inline-flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2 h-7 px-2">
        <ArrowLeft />
        Products
      </Button>
      <span>{productName}</span>
    </span>
  ) : (
    'Projects'
  );
  const headerDesc = productId
    ? 'Projects under this product.'
    : 'All projects, optionally grouped by product.';

  return (
    <ItemGroup>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ItemGroupHeader
        title={headerTitle}
        description={headerDesc}
        action={
          <div className="flex items-center gap-2">
            {!productId && (
              <Select value={filter} onValueChange={(v) => setFilter(v)}>
                <SelectTrigger className="h-8 w-auto min-w-[160px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All projects</SelectItem>
                  <SelectItem value="unlinked">No product</SelectItem>
                  {products.length > 0 && <SelectSeparator />}
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button size="sm" onClick={() => setDialog({ kind: 'edit', project: null })}>
              <Plus />
              New
            </Button>
          </div>
        }
      />

      <ItemGroupContent>
        {loading && items.length === 0 && <ItemGroupEmpty>loading…</ItemGroupEmpty>}
        {!loading && items.length === 0 && (
          <ItemGroupEmpty>
            {productId ? 'no projects in this product yet' : 'no projects yet'}
          </ItemGroupEmpty>
        )}

        {items.map((p) => (
          <Item key={p.id}>
            <ItemMedia>
              <FolderOpen />
            </ItemMedia>
            <ItemContent>
              <ItemHeader>
                <ItemTitle>{p.name}</ItemTitle>
                <code className="text-xs text-muted-foreground">{p.slug}</code>
                {!productId &&
                  (p.productId ? (
                    <span className="text-[11px] text-muted-foreground">
                      · {productMap.get(p.productId)?.name ?? p.productId.slice(0, 8)}
                    </span>
                  ) : (
                    <span className="text-[11px] italic text-muted-foreground">· no product</span>
                  ))}
              </ItemHeader>
              {(p.trackerKind || p.trackerRef) && (
                <ItemDescription>
                  tracker: {p.trackerKind ?? '?'} · {p.trackerRef ?? '—'}
                </ItemDescription>
              )}
            </ItemContent>
            <ItemActions>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialog({ kind: 'prd', project: p })}
              >
                <FileText />
                PRD
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialog({ kind: 'techspec', project: p })}
              >
                <FileText />
                TechSpec
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialog({ kind: 'notes', project: p })}
              >
                <NotebookPen />
                Notes
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialog({ kind: 'edit', project: p })}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  if (!(await showConfirm(`Delete project "${p.name}"?`))) return;
                  try {
                    await api.del(`/api/projects/${p.id}`);
                    void reload();
                  } catch (e) {
                    void showAlert((e as { message: string }).message);
                  }
                }}
              >
                <Trash2 />
              </Button>
            </ItemActions>
          </Item>
        ))}
      </ItemGroupContent>

      <ProjectEditorDialog
        open={dialog?.kind === 'edit'}
        defaultProductId={productId}
        products={products}
        initial={dialog?.kind === 'edit' ? dialog.project : null}
        onOpenChange={(open) => !open && setDialog(null)}
        onSaved={() => {
          setDialog(null);
          void reload();
        }}
      />

      <MdEditorDialog
        open={dialog?.kind === 'prd'}
        title={dialog?.kind === 'prd' ? `${dialog.project.name} · PRD.md` : ''}
        initialBody={dialog?.kind === 'prd' ? dialog.project.prdMd : ''}
        onOpenChange={(open) => !open && setDialog(null)}
        onSave={async (body) => {
          if (dialog?.kind !== 'prd') return;
          await api.patch(`/api/projects/${dialog.project.id}/prd`, { prd_md: body });
          void reload();
        }}
      />
      <MdEditorDialog
        open={dialog?.kind === 'techspec'}
        title={dialog?.kind === 'techspec' ? `${dialog.project.name} · TechSpec.md` : ''}
        initialBody={dialog?.kind === 'techspec' ? dialog.project.techspecMd : ''}
        onOpenChange={(open) => !open && setDialog(null)}
        onSave={async (body) => {
          if (dialog?.kind !== 'techspec') return;
          await api.patch(`/api/projects/${dialog.project.id}/techspec`, { techspec_md: body });
          void reload();
        }}
      />

      <NotesPanelDialog
        open={dialog?.kind === 'notes'}
        project={dialog?.kind === 'notes' ? dialog.project : null}
        onOpenChange={(open) => !open && setDialog(null)}
      />
    </ItemGroup>
  );
}

function ProjectEditorDialog({
  open,
  defaultProductId,
  products,
  initial,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  defaultProductId?: string | undefined;
  products: ProductRef[];
  initial: Project | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [productId, setProductId] = useState<string>('');
  const [trackerKind, setTrackerKind] = useState<'' | 'yandex' | 'linear'>('');
  const [trackerRef, setTrackerRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setSlug(initial?.slug ?? '');
    setProductId(initial?.productId ?? defaultProductId ?? '');
    setTrackerKind((initial?.trackerKind as '' | 'yandex' | 'linear') ?? '');
    setTrackerRef(initial?.trackerRef ?? '');
    setErr(null);
  }, [open, initial, defaultProductId]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {
        name,
        slug,
        product_id: productId || null,
        ...(trackerKind ? { tracker_kind: trackerKind } : {}),
        ...(trackerRef ? { tracker_ref: trackerRef } : {}),
      };
      if (initial) await api.patch(`/api/projects/${initial.id}`, body);
      else
        await api.post('/api/projects', {
          prd_md: '',
          techspec_md: '',
          ...body,
        });
      onSaved();
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? `Edit "${initial.name}"` : 'New project'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="pr-name">Name</Label>
            <Input id="pr-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pr-slug">Slug</Label>
            <Input id="pr-slug" value={slug} onChange={(e) => setSlug(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Product (optional)</Label>
            <Select
              value={productId || 'none'}
              onValueChange={(v) => setProductId(v === 'none' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">(no product)</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Tracker kind</Label>
            <Select
              value={trackerKind || 'none'}
              onValueChange={(v) => setTrackerKind(v === 'none' ? '' : (v as 'yandex' | 'linear'))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">(none)</SelectItem>
                <SelectItem value="yandex">yandex</SelectItem>
                <SelectItem value="linear">linear</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pr-tref">Tracker ref</Label>
            <Input
              id="pr-tref"
              value={trackerRef}
              onChange={(e) => setTrackerRef(e.target.value)}
              placeholder="ABC-123 or URL"
            />
          </div>
          {err && <ErrorBanner>{err}</ErrorBanner>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || !name || !slug} onClick={save}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MdEditorDialog({
  open,
  title,
  initialBody,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  title: string;
  initialBody: string;
  onOpenChange: (open: boolean) => void;
  onSave: (body: string) => Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBody(initialBody);
    setErr(null);
  }, [open, initialBody]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="min-h-[400px] font-mono text-xs"
        />
        {err && <ErrorBanner>{err}</ErrorBanner>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await onSave(body);
                onOpenChange(false);
              } catch (e) {
                setErr((e as { message: string }).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotesPanelDialog({
  open,
  project,
  onOpenChange,
}: {
  open: boolean;
  project: Project | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = async () => {
    if (!project) return;
    try {
      setNotes(await api.get<ProjectNote[]>(`/api/projects/${project.id}/notes`));
    } catch (e) {
      setErr((e as { message: string }).message);
    }
  };

  useEffect(() => {
    if (!open || !project) return;
    setDraft('');
    setErr(null);
    void reload();
  }, [open, project?.id]);

  const append = async () => {
    if (!project || !draft.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/projects/${project.id}/notes`, { body: draft });
      setDraft('');
      void reload();
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{project ? `${project.name} · Notes` : 'Notes'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="note-body">New note</Label>
          <Textarea id="note-body" value={draft} onChange={(e) => setDraft(e.target.value)} />
          <div className="flex justify-end">
            <Button disabled={busy || !draft.trim()} onClick={append}>
              Append
            </Button>
          </div>
        </div>
        {err && <ErrorBanner>{err}</ErrorBanner>}
        <div className="space-y-2">
          {notes.length === 0 && (
            <div className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground">
              no notes yet
            </div>
          )}
          {notes.map((n) => (
            <Card key={n.id}>
              <CardContent className="space-y-1 p-3">
                <div className="font-mono text-xs text-muted-foreground">
                  {n.createdAt.slice(0, 19)}
                </div>
                <div className="whitespace-pre-wrap text-sm">{n.body}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
